import React from 'react'
import { PORT_NUMBER_OFFSET_PX, inPortPoint } from '@/lib/text-linking/text-linking-geometry'

import { ChainConnectors } from './chain-connectors'
import { FrameBodyHitAreas } from './frame-body-hit-areas'
import { FrameOutPort } from './frame-out-port'
import { LinkTargetOutline } from './link-target-outline'
import { LinkingRubberBand } from './linking-rubber-band'
import { PortGroup } from './port-group'
import { PortSquare } from './port-square'
import { TextToolHoverPort } from './text-tool-hover-port'
import { chromeFrameOf } from '../helpers/chrome-frame-of'
import { hasIncoming, hasOutgoing, resolvePortFrames } from '../helpers/port-frames'
import {
  linkTargetLabel,
  lockedLinkLabel,
  startLinkLabel,
  unlinkLabel,
} from '../helpers/port-labels'
import { usePortFocus } from '../hooks/use-port-focus'
import type { PortControlProps } from '../types/port-control'
import type { TextLinkingLayerProps } from '../types/text-linking-layer-props'

export type { TextLinkingLayerProps, TextLinkingModeState } from '../types/text-linking-layer-props'

/** Stable identity so the default prop value never re-triggers a memo. */
const EMPTY_FRAME_ID_SET: ReadonlySet<string> = new Set<string>()

// Gap between a link-target frame's edge and its dashed hover outline. SCREEN px — see
// `targetOutlineInset`, which converts it to doc px.
const TARGET_OUTLINE_INSET_PX = 2

/**
 * SVG overlay rendering text-link connectors, numbered in-ports, and interactive in/out ports
 * for linking mode. Mounted in screen space, a sibling of the canvas's `transform: scale()`
 * wrapper — same as the visual layer's selection chrome (see this file's mount comment in
 * `center-canvas/components/screen-space-layers.tsx`) — so every measurement drawn here is a
 * literal on-screen pixel.
 */
export function TextLinkingLayer({
  chains,
  allTextFrames,
  selectedTextIds,
  showTextLinking,
  scale,
  linking,
  selectToolActive,
  textToolActive = false,
  textToolHoverFrameId = null,
  onOutPortClick,
  onInPortClick,
  onUnlinkClick,
  onPortHoverChange,
  onCancelLinking,
  overflowBadgeFrameIds = EMPTY_FRAME_ID_SET,
}: TextLinkingLayerProps): React.JSX.Element {
  const linkingActive = linking != null
  // The numbered ports' outward push, the link-target hover outline's inset, and the connector
  // endpoints that have to stay attached to them are all already screen px — no conversion needed
  // now that this layer draws in screen space directly.
  const portNumberOffset = PORT_NUMBER_OFFSET_PX
  const targetOutlineInset = TARGET_OUTLINE_INSET_PX
  const {
    linkableFrames,
    seqByFrameId,
    portFrames,
    portFrameIds,
    lockedOutPortFrames,
    lockedOutPortFrameIds,
  } = resolvePortFrames({ chains, allTextFrames, selectedTextIds, linkingActive, selectToolActive })
  const [hoveredUnlinkFrameId, setHoveredUnlinkFrameId] = React.useState<string | null>(null)
  const { buildPortControl, requestFocus } = usePortFocus({ portFrames, linking, onCancelLinking })

  return (
    // `presentation`: this SVG is a container for controls, not a graphic in its own right. It must
    // NOT be `aria-hidden` — hiding an ancestor of focusable content is itself a violation.
    <svg
      className="absolute inset-0 overflow-visible"
      style={{ pointerEvents: 'none' }}
      width="100%"
      height="100%"
      role="presentation"
    >
      {/* Connectors + passive numbered in-ports — gated only by the View menu toggle / active
          linking mode, not selection (WMPCM-6852 AC6: the design shows these surviving
          deselection). */}
      {(showTextLinking || linkingActive) && (
        <ChainConnectors
          chains={chains}
          scale={scale}
          portNumberOffset={portNumberOffset}
          portFrameIds={portFrameIds}
          lockedOutPortFrameIds={lockedOutPortFrameIds}
          seqByFrameId={seqByFrameId}
        />
      )}

      {linking?.cursorDocPoint && (
        <LinkingRubberBand
          sourceId={linking.sourceId}
          cursorDocPoint={linking.cursorDocPoint}
          allTextFrames={allTextFrames}
          scale={scale}
        />
      )}

      {/* Interactive ports, in two passes over the same frames: out-ports first, then in-ports, so
          a receiving frame's number always paints *above* the preceding frame's out-port square.
          Two linked frames sitting close together put those two badges within one square's width of
          each other, and a single interleaved pass would order them by `allTextFrames` position —
          arbitrarily hiding one or the other. */}
      {portFrames.map((frame) => {
        const seq = seqByFrameId.get(frame.id)
        const chrome = chromeFrameOf(frame, scale)
        const linked = hasOutgoing(chains, frame.id)
        const inLinked = hasIncoming(chains, frame.id)
        const isHover = linking?.hoverTargetId === frame.id
        const isSource = linking?.sourceId === frame.id
        const rotationDeg = frame.rotation ?? 0
        const outControl = buildPortControl(
          'out',
          frame.id,
          linked ? unlinkLabel(seq) : startLinkLabel(seq),
          () => {
            if (linked) {
              onUnlinkClick(frame.id)
              // This edge re-renders as a "+" — stay on it, so the gesture can be reversed
              // without hunting the port down again.
              requestFocus({ kind: 'port', edge: 'out', frameIds: [frame.id] })
            } else {
              onOutPortClick(frame.id)
              requestFocus({ kind: 'first-target' })
            }
          },
          !linkingActive
        )

        return (
          <PortGroup key={`out-${frame.id}`}>
            {/* Outline only — the matching HIT rect is rendered in its own pass at the very end of
                this layer. The two are split so paint order and hit-test order can differ: the
                outline has to stay down here (under the port badges, which must paint over it),
                while the hit area has to sit above the in-port squares. See that pass for why. */}
            {linkingActive && frame.id !== linking?.sourceId && (
              <LinkTargetOutline chrome={chrome} inset={targetOutlineInset} isHover={isHover} />
            )}
            {/* Out-port: "+" to start linking, solid square (hover = "×") to unlink when already
                linked, hidden while this frame is the active source. */}
            {!isSource && (
              <FrameOutPort
                chrome={chrome}
                portNumberOffset={portNumberOffset}
                rotationDeg={rotationDeg}
                linked={linked}
                inLinked={inLinked}
                seq={seq}
                showContainer={!overflowBadgeFrameIds.has(frame.id)}
                control={outControl}
                hovered={hoveredUnlinkFrameId === frame.id}
                onUnlinkClick={() => onUnlinkClick(frame.id)}
                onHoverChange={(hovered) => setHoveredUnlinkFrameId(hovered ? frame.id : null)}
                plusCursor="pointer"
                onPlusClick={() => onOutPortClick(frame.id)}
              />
            )}
          </PortGroup>
        )
      })}

      {lockedOutPortFrames.map((frame) => {
        const seq = seqByFrameId.get(frame.id)
        const chrome = chromeFrameOf(frame, scale)
        const control: PortControlProps = {
          ...buildPortControl('out', frame.id, lockedLinkLabel(seq), () => {}),
          disabled: true,
        }

        return (
          <PortGroup key={`locked-out-${frame.id}`}>
            <FrameOutPort
              chrome={chrome}
              portNumberOffset={portNumberOffset}
              rotationDeg={frame.rotation ?? 0}
              linked={hasOutgoing(chains, frame.id)}
              inLinked={hasIncoming(chains, frame.id)}
              seq={seq}
              showContainer={!overflowBadgeFrameIds.has(frame.id)}
              control={control}
              hovered={false}
              onUnlinkClick={() => {}}
              onHoverChange={() => {}}
              // The chain's tail: the same "+" an unlocked tail shows, inert. No click handler, and
              // the not-allowed cursor says so.
              plusCursor="not-allowed"
            />
          </PortGroup>
        )
      })}

      {portFrames.map((frame) => {
        const seq = seqByFrameId.get(frame.id)
        const inLinked = hasIncoming(chains, frame.id)
        const chrome = chromeFrameOf(frame, scale)
        // Numbered badges get pushed outward to clear the frame's text; a bare (unlinked) port sits
        // on the border. Mirrors the out-port's own offset choice (`FrameOutPort`).
        //
        // Keyed on `inLinked`, the same predicate the `seq` prop below uses — NOT on `seq != null`
        // (WMPCM-9199). `chainSequenceByFrameId` numbers every chain member including the head, but
        // a head has nothing incoming, so it renders no numeral here; keying the offset off `seq`
        // pushed its in-port 6px off the border while showing nothing, an unexplained jump the
        // moment a frame became a chain head. The two must stay the same condition.
        const inP = inLinked
          ? inPortPoint(chrome, portNumberOffset, chrome.pivot)
          : inPortPoint(chrome, undefined, chrome.pivot)
        // Only a live session's *other* frames are real link targets. The source's own in-port stays
        // clickable (a click there just ends the session — `handleTextInPortClick` refuses to link a
        // frame to itself), but it is no keyboard destination, so it gets no control.
        const isLinkTarget = linkingActive && frame.id !== linking?.sourceId
        const inControl = isLinkTarget
          ? buildPortControl('in', frame.id, linkTargetLabel(seq), () => {
              const sourceId = linking?.sourceId

              onInPortClick(frame.id)
              // Prefer the frame just linked INTO — its out-port is the next step of a chain, so
              // this leaves the user positioned to keep going. Falls back to the source's out-port
              // (now the unlink control) when the target's own ports did not survive the parent's
              // post-link reselect.
              requestFocus({
                kind: 'port',
                edge: 'out',
                frameIds: sourceId ? [frame.id, sourceId] : [frame.id],
              })
            })
          : undefined

        return (
          <PortGroup key={`in-${frame.id}`}>
            <PortSquare
              center={inP}
              rotationDeg={frame.rotation ?? 0}
              // Only stamp the sequence number once a link actually lands on this port — an open
              // in-port, e.g. a chain head's, has nothing incoming to number yet, and the head's
              // number belongs at its out-port instead.
              seq={inLinked ? seq : undefined}
              // Only a live linking session can act on an in-port — `handleTextInPortClick`
              // (center-canvas/hooks/use-text-linking.ts) early-returns with no link in progress. Staying inert
              // otherwise matters now that this square paints over the previous frame's out-port:
              // it must not swallow clicks aimed at that unlink target when the two overlap.
              interactive={linkingActive}
              cursor={linkingActive ? 'pointer' : 'default'}
              onClick={() => onInPortClick(frame.id)}
              control={inControl}
            />
          </PortGroup>
        )
      })}

      {/* Frame-body HIT areas for an active linking session — LAST, so they hit-test above every
          in-port square. See `FrameBodyHitAreas` for why. */}
      {linkingActive && (
        <FrameBodyHitAreas
          portFrames={portFrames}
          sourceId={linking?.sourceId}
          scale={scale}
          onInPortClick={onInPortClick}
          onPortHoverChange={onPortHoverChange}
        />
      )}

      {textToolActive && !linkingActive && textToolHoverFrameId && (
        <TextToolHoverPort
          frameId={textToolHoverFrameId}
          linkableFrames={linkableFrames}
          chains={chains}
          scale={scale}
          overflowBadgeFrameIds={overflowBadgeFrameIds}
          onOutPortClick={onOutPortClick}
        />
      )}
    </svg>
  )
}

export default TextLinkingLayer
